import { api } from '@/api/api';

export interface AddEmployeeCompetencyPayload {
    competencyId: string;
    employeeIds: string[];
}

export interface RemoveEmployeeCompetencyPayload {
    competencyId: string;
    employeeIds: string[];
}

export interface EmployeesWithCompetencyPayload {
    competencyId: string;
    employeeIds: string[];
}

// Per-employee overlay on top of the assigned profile: the FULL desired set of
// individually-added and individually-removed competencies for one employee (not a
// delta). Used by the roster page's per-employee customization.
export interface EmployeeCompetencyOverlayPayload {
    employeeId: string;
    addedCompetencyIds: string[];
    removedCompetencyIds: string[];
}

// The effective competencies for an employee: profile competencies minus overlay
// removals, plus overlay additions. Returned by GET /EmployeeCompetency/{id}/effective.
export interface EffectiveCompetency {
    competencyId: string;
    name: Record<string, string>;
    source: 'FromProfile' | 'Individual';
}

export default {
    async addEmployeeCompetency(payload: AddEmployeeCompetencyPayload): Promise<void> {
        await api.post('/EmployeeCompetency/add', payload);
    },

    async removeEmployeeCompetency(payload: RemoveEmployeeCompetencyPayload): Promise<void> {
        await api.post('/EmployeeCompetency/remove', payload);
    },

    // Returns the subset of `employeeIds` that already effectively have the competency
    // (via their assigned profile and not removed, or via an individual addition).
    async getEmployeesWithCompetency(payload: EmployeesWithCompetencyPayload): Promise<string[]> {
        const response = await api.post('/EmployeeCompetency/with-competency', payload);

        return response.data;
    },

    // Returns the ids of employees who have the competency added individually (a Kind=Added
    // overlay), i.e. outside of any profile. Backs the "Profils assignés" individual drill-in.
    async getIndividualEmployees(competencyId: string): Promise<string[]> {
        const response = await api.get(`/EmployeeCompetency/${competencyId}/individual-employees`);

        return response.data;
    },

    // Replaces one employee's competency overlay with the FULL desired state. The
    // backend reconciles the added/removed sets against the employee's profile.
    async setEmployeeCompetencyOverlay(payload: EmployeeCompetencyOverlayPayload): Promise<void> {
        await api.put('/EmployeeCompetency/overlay', payload);
    },

    // Returns the employee's effective competencies: profile minus removals plus additions.
    async getEffectiveCompetencies(employeeId: string): Promise<EffectiveCompetency[]> {
        const response = await api.get(`/EmployeeCompetency/${employeeId}/effective`);
        return response.data;
    },
};
